import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Button, theme, MenuProps, Badge, Popover, List, Typography, Space, Tag, Drawer, Grid } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  UserOutlined,
  AppstoreOutlined,
  FacebookOutlined,
  RobotOutlined,
  FileTextOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  TeamOutlined,
  SettingOutlined,
  DollarOutlined,
  BarChartOutlined,
  ShoppingOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../types';
import { postsService } from '../../services/posts.service';
import config from '../../config';

const { Text } = Typography;
const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;

const formatRelativeTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'Vừa xong';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} phút trước`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} giờ trước`;
  return `${Math.floor(diffInSeconds / 86400)} ngày trước`;
};

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [queueItems, setQueueItems] = useState<any[]>([]);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchQueue = async () => {
    try {
      const data = await postsService.getQueue();
      setQueueItems(data);
    } catch (err) {
      console.error('Failed to fetch queue', err);
    }
  };

  useEffect(() => {
    fetchQueue();

    const token = localStorage.getItem('token');
    const sseUrl = `${config.API_URL}/posts/queue/events?token=${token}`;
    const eventSource = new EventSource(sseUrl);

    eventSource.onmessage = (event) => {
      try {
        const updatedPost = JSON.parse(event.data);
        setQueueItems(prev => {
          const exists = prev.some(item => item.id === updatedPost.id);
          if (exists) {
            return prev.map(item => item.id === updatedPost.id ? updatedPost : item);
          } else {
            return [updatedPost, ...prev].slice(0, 20);
          }
        });
      } catch (err) {
        console.error('Failed to parse SSE message', err);
      }
    };

    eventSource.onerror = (err) => {
      console.log('SSE connection closed or error occurred. EventSource will auto-reconnect.', err);
    };

    return () => {
      eventSource.close();
    };
  }, []);

  const pendingCount = queueItems.filter(i => i.status === 'pending').length;
  const failedCount = queueItems.filter(i => i.status === 'failed').length;

  const queueContent = (
    <div style={{ width: isMobile ? '85vw' : 350, maxHeight: 400, overflowY: 'auto' }}>
      <List
        dataSource={queueItems}
        locale={{ emptyText: 'Không có tiến trình nào đang chạy' }}
        renderItem={(item) => (
            <List.Item 
              style={{ 
                cursor: item.permalinkUrl ? 'pointer' : 'default',
                transition: 'background 0.3s',
                padding: '12px 16px'
              }}
              className="queue-item-hover"
              onClick={() => {
                if (item.permalinkUrl) {
                  window.open(item.permalinkUrl, '_blank');
                }
              }}
            >
              <List.Item.Meta
                avatar={<Avatar src={item.page?.pictureUrl} icon={<FacebookOutlined />} />}
                title={
                  <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                    <Text strong style={{ fontSize: isMobile ? 12 : 14 }}>{item.page?.name || 'Trang Facebook'}</Text>
                    {item.status === 'pending' ? (
                      <Tag color="blue" icon={<LoadingOutlined spin />} style={{ fontSize: 10 }}>Đang đợi</Tag>
                    ) : item.status === 'published' ? (
                      <Tag color="success" icon={<CheckCircleOutlined />} style={{ fontSize: 10 }}>Thành công</Tag>
                    ) : (
                      <Tag color="error" icon={<CloseCircleOutlined />} style={{ fontSize: 10 }}>Thất bại</Tag>
                    )}
                  </Space>
                }
                description={
                  <div style={{ fontSize: 11 }}>
                    <div style={{ color: '#666', marginBottom: 4 }}>
                      {item.content?.message?.substring(0, isMobile ? 30 : 50)}{item.content?.message?.length > (isMobile ? 30 : 50) ? '...' : ''}
                    </div>
                    <Space split={<span style={{ color: '#ccc' }}>|</span>}>
                      <Text type="secondary">{formatRelativeTime(item.createdAt)}</Text>
                      {item.errorMessage && <Text type="danger" style={{ fontSize: 10 }}>{item.errorMessage}</Text>}
                    </Space>
                  </div>
                }
              />
            </List.Item>
        )}
      />
      {queueItems.length > 0 && (
        <div style={{ padding: '8px 0', textAlign: 'center', borderTop: '1px solid #f0f0f0' }}>
          <Button type="link" onClick={() => { navigate('/posts/list'); setMobileMenuOpen(false); }}>Xem tất cả bài viết</Button>
        </div>
      )}
    </div>
  );

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
    if (isMobile) setMobileMenuOpen(false);
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      label: 'Cài đặt tài khoản',
      icon: <UserOutlined />,
      onClick: () => { navigate('/settings/profile'); if (isMobile) setMobileMenuOpen(false); },
    },
    ...(user?.role === UserRole.ADMIN ? [
      {
        key: 'admin-users',
        label: 'Quản lý người dùng',
        icon: <TeamOutlined />,
        onClick: () => { navigate('/admin/users'); if (isMobile) setMobileMenuOpen(false); },
      }
    ] : []),
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Đăng xuất',
      icon: <LogoutOutlined />,
      onClick: logout,
    },
  ];

  const userMenu: MenuProps = {
    items: userMenuItems,
  };

  const hasPermission = (permission: string) => {
    if (user?.role === UserRole.ADMIN) return true;
    return user?.permissions?.includes(permission);
  };

  const menuItems = [
    ...(hasPermission('dashboard') ? [{
      key: '/',
      icon: <AppstoreOutlined />,
      label: 'Bảng điều khiển',
    }] : []),
    ...(hasPermission('pages') ? [{
      key: '/pages',
      icon: <FacebookOutlined />,
      label: 'Trang của tôi',
    }] : []),
    ...(hasPermission('products') ? [{
      key: '/products/list',
      icon: <ShoppingOutlined />,
      label: 'Sản phẩm',
    }] : []),
    ...(hasPermission('posts') ? [{
      key: 'posts',
      icon: <FileTextOutlined />,
      label: 'Bài viết',
      children: [
        {
          key: '/posts/create',
          label: 'Đăng bài',
        },
        {
          key: '/posts/list',
          label: 'Danh sách bài đã đăng',
        },
      ],
    }] : []),
    ...(hasPermission('auto-post') ? [{
      key: '/auto-post',
      icon: <RobotOutlined />,
      label: 'Tự động đăng bài',
    }] : []),
    ...(hasPermission('ads') ? [{
      key: 'ads',
      icon: <DollarOutlined />,
      label: 'Quảng cáo',
      children: [
        {
          key: '/ads/accounts',
          label: 'Tài khoản QC',
        },
        {
          key: '/ads/list',
          label: 'Quản lý QC',
        },
        {
          key: '/ads/reports',
          icon: <BarChartOutlined />,
          label: 'Báo cáo',
        },
      ],
    }] : []),
    ...(user?.role === UserRole.ADMIN ? [
      {
        type: 'divider' as const,
        key: 'admin-divider',
      },
      {
        key: 'admin',
        icon: <SettingOutlined />,
        label: 'Quản trị hệ thống',
        children: [
          {
            key: '/admin/users',
            icon: <TeamOutlined />,
            label: 'Người dùng',
          },
        ],
      },
    ] : []),
  ];

  const sideMenu = (
    <Menu
      theme={isMobile ? "light" : "dark"}
      mode="inline"
      defaultSelectedKeys={[location.pathname]}
      onClick={handleMenuClick}
      items={menuItems}
    />
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {!isMobile && (
        <Sider trigger={null} collapsible collapsed={collapsed}>
          <div style={{ height: 64, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, overflow: 'hidden' }}>
            <img src="/logo.jpg" alt="Logo" style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0 }} />
            {!collapsed && <span style={{ color: '#fff', fontSize: 16, fontWeight: 'bold', whiteSpace: 'nowrap' }}>4AE Content</span>}
          </div>
          {sideMenu}
        </Sider>
      )}

      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src="/logo.jpg" alt="Logo" style={{ width: 32, height: 32, borderRadius: '50%' }} />
            <span style={{ fontSize: 16, fontWeight: 'bold' }}>4AE Content</span>
          </div>
        }
        placement="left"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        bodyStyle={{ padding: 0 }}
        width={250}
      >
        {sideMenu}
      </Drawer>

      <Layout>
        <Header style={{ 
          padding: isMobile ? '0 12px' : 0, 
          background: colorBgContainer, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          paddingRight: isMobile ? 12 : 24,
          position: 'sticky',
          top: 0,
          zIndex: 100,
          boxShadow: '0 1px 4px rgba(0,21,41,.08)'
        }}>
          {isMobile ? (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setMobileMenuOpen(true)}
              style={{ fontSize: '18px', width: 48, height: 48 }}
            />
          ) : (
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: '16px', width: 64, height: 64 }}
            />
          )}

          {isMobile && !mobileMenuOpen && (
            <div style={{ flex: 1, textAlign: 'center' }}>
               <span style={{ fontWeight: 'bold', fontSize: 16 }}>4AE</span>
            </div>
          )}
          
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 20 }}>
            <Popover 
              content={queueContent} 
              title={<Text strong>Tiến trình đăng bài</Text>}
              trigger="click" 
              placement="bottomRight"
            >
              <Badge count={pendingCount} overflowCount={99} size="small" offset={[-2, 4]}>
                <Button 
                  type="text" 
                  icon={<SyncOutlined spin={pendingCount > 0} />} 
                  style={{ fontSize: isMobile ? 18 : 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                />
              </Badge>
            </Popover>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {!isMobile && <span style={{ fontWeight: 500 }}>{user?.name}</span>}
              <Dropdown menu={userMenu} placement="bottomRight">
                <Avatar 
                  size={isMobile ? 'small' : 'default'} 
                  src={user?.profilePicture} 
                  icon={<UserOutlined />} 
                  style={{ cursor: 'pointer' }} 
                />
              </Dropdown>
            </div>
          </div>
        </Header>
        <Content
          style={{
            margin: isMobile ? '12px 8px' : '24px 16px',
            padding: isMobile ? 12 : 24,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
